import { sendToApi } from 'api/client'
import { Comment, WorkflowReferenceable } from 'types'
import { ourUuid } from 'utils'
import { type Task } from 'api/clients/TaskClient'
import { PaginatedApiClient } from 'api/PaginatedApiClient'
import { PaginationParams } from 'unsplash-js/dist/types/request'
import { ChannelType } from 'components/CampaignCreationModal/Types'

export interface TodoUser {
  id: number
  avatar_url: string | null
  display_name: string
  first_name?: string
}

export interface TodoDocument {
  last_edited_at?: string | null
  last_edited_by: string | null
  id?: string
  key: string
  title: string
  channel_emoji?: ChannelType | null
  url: string
  header_emoji: string | null
  external_file_icon?: string | null
}

export interface TaskSection {
  id: number | undefined
  name: string
  position: number
}

export enum TasksFilter {
  MyTasks = 'todo',
  FollowUp = 'followup',
  Completed = 'completed',
}

export interface TasksCounts {
  [TasksFilter.MyTasks]: number
  [TasksFilter.FollowUp]: number
  [TasksFilter.Completed]: number
}

interface TodoCreator {
  id: number
  uuid?: string
  type: 'User' | 'AnonymousUser'
  display_name: string
  initials: string
  avatar_url: string | null
}

export interface TodoReaction {
  id: number
  reaction: string
  user_id: number
  user_name: string
  todo_id: number
}

export interface Todo extends WorkflowReferenceable {
  article_last_edited_by?: string | null
  article_last_edited_at?: string | number | null
  id: number
  created_at: string
  creator: TodoCreator
  read_status?: boolean
  uuid?: string | null
  important: boolean
  position: number
  title: string
  body: string
  article_id: string
  article_header_emoji: string | null
  external_file_icon: string | null
  article_url: string | null
  article_title: string | null
  content: string
  requester: TodoUser
  assignee: TodoUser | null
  due_date: string | null
  mentioned_user_ids: number[]
  completed_at: string | null
  comments_count: number
  unread_comments_count: number
  html_title: string
  description: string
  html_description: string
  section_id: number | undefined
  workspace_id: number
  tasks?: Task[]
  pinned?: boolean
  add_to_bottom?: boolean
  add_to_position?: boolean
  comments: Comment[]
  reactions: TodoReaction[]
  informal_feedback_request_id?: number | null
}

export interface CreateTodoInput {
  title: string
  htmlTitle: string
  description: string
  htmlDescription: string
  dueDate?: string | null
  assigneeId?: number | null
  mentionedIds: number[]
  from?: number
  to?: number
  workspace_id?: number
  position?: number
  sectionId?: number
}

export interface UpdateTodoInput extends Partial<CreateTodoInput> {
  articleId?: string | null
  updatedArticle?: boolean
}

export interface UpdateTodoSectionInput {
  name?: string
  position?: number | undefined
}

const getTodos = (
  { dueBefore, dueAfter }: { dueBefore?: Date | string; dueAfter?: Date | string },
  workspaceId: number,
  path: string,
) => {
  const searchParams = new URLSearchParams()

  if (dueBefore) searchParams.set('due_before', dueBefore.toString())
  if (dueAfter) searchParams.set('due_after', dueAfter.toString())
  if (!dueBefore && !dueAfter) searchParams.set('anytime', 'true')

  return new PaginatedApiClient<{ todos: Todo[] }>(`/w/${workspaceId}${path}?${searchParams.toString()}`)
}

export const TodoClient = {
  getTodos: (articleId: string) => {
    return sendToApi<{ todos: Todo[] }>(`/articles/${articleId}/todos`)
  },

  getWorkspaceTodos: (workspaceId: number, params: {}) => {
    const searchParams = new URLSearchParams(params)
    return sendToApi<{ todos: Todo[] }>(`/w/${workspaceId}/todos?${params ? searchParams.toString() : ''}`)
  },

  getPaginatedWorkspaceTodos: (workspaceId: number, params: {}) => {
    const searchParams = new URLSearchParams(params)
    return new PaginatedApiClient<{ todos: Todo[] }>(
      `/w/${workspaceId}/todos/all_tasks?${params ? searchParams.toString() : ''}`,
    )
  },

  getDueTodos: (
    { dueBefore, dueAfter }: { dueBefore?: Date | string; dueAfter?: Date | string },
    workspaceId: number,
  ) => {
    return getTodos({ dueBefore, dueAfter }, workspaceId, '/todos/due')
  },

  getTodosCount: (workspaceId: number) => sendToApi<TasksCounts>(`/w/${workspaceId}/todos/counts`),

  getFollowupTodos: (
    { dueBefore, dueAfter }: { dueBefore?: Date | string; dueAfter?: Date | string },
    workspaceId: number,
  ) => {
    return getTodos({ dueBefore, dueAfter }, workspaceId, '/todos/followup')
  },

  getPinnedTodos: (workspaceId: number) => {
    return getTodos({}, workspaceId, '/todos/pinned')
  },

  getCompletedTodos: (
    { completedBefore, completedAfter }: { completedBefore?: Date; completedAfter?: Date },
    workspaceId: number,
  ) => {
    const searchParams = new URLSearchParams()

    if (completedBefore) searchParams.set('completed_before', completedBefore.toISOString())
    if (completedAfter) searchParams.set('completed_after', completedAfter.toISOString())
    if (!completedBefore && !completedAfter) searchParams.set('anytime', 'true')

    return new PaginatedApiClient<{ todos: Todo[] }>(`/w/${workspaceId}/todos/completed?${searchParams.toString()}`)
  },

  createTodo: (articleId: string, input: CreateTodoInput, attachedToContent: boolean = false) => {
    const uuid = attachedToContent ? ourUuid() : null
    const {
      title,
      htmlTitle,
      description,
      htmlDescription,
      dueDate,
      assigneeId,
      workspace_id,
      mentionedIds,
      from,
      to,
    } = input

    return sendToApi<{ todo: Todo }>(`/articles/${articleId}/todos`, {
      method: 'POST',
      body: {
        due_date: dueDate,
        assignee_id: assigneeId,
        workspace_id: workspace_id,
        mentioned_user_ids: mentionedIds,
        content: title,
        html_title: htmlTitle,
        description: description,
        html_description: htmlDescription,
        uuid,
        from_character: from,
        to_character: to,
      },
    })
  },

  createMinimalTodo: ({
    articleId,
    title,
    assigneeId,
    dueDate,
    sectionId,
    workspaceId,
    pinned,
    position,
  }: {
    workspaceId: number
    sectionId: number | undefined
    articleId: string | null
    title: string
    assigneeId: number | undefined
    dueDate: string | null
    pinned?: boolean
    position?: number
  }) => {
    return sendToApi<{ todo: Todo }>(`/w/${workspaceId}/todos`, {
      method: 'POST',
      body: {
        article_id: articleId,
        workspace_id: workspaceId,
        section_id: sectionId,
        due_date: dueDate,
        position: position,
        assignee_id: assigneeId,
        content: title,
        pinned,
      },
    })
  },

  updateTodo: (todoId: number, input: UpdateTodoInput) => {
    const {
      title,
      htmlTitle,
      description,
      htmlDescription,
      dueDate,
      assigneeId,
      mentionedIds,
      position,
      sectionId,
      articleId,
      updatedArticle,
    } = input
    return sendToApi<{ todo: Todo }>(`/todos/${todoId}/`, {
      method: 'PUT',
      body: {
        updated_article: updatedArticle,
        todo: {
          content: title,
          position: position,
          section_id: sectionId,
          html_title: htmlTitle,
          description: description,
          html_description: htmlDescription,
          due_date: dueDate,
          assignee_id: assigneeId,
          article_id: articleId,
          mentioned_user_ids: mentionedIds,
        },
      },
    })
  },

  completeTodo: (todoId: number) => {
    return sendToApi<{ todo: Todo }>(`/todos/${todoId}/complete`, {
      method: 'PUT',
    })
  },

  batchCompleteTodos: (ids: number[] = []) => {
    return sendToApi<{ todo: Todo[] }>(`/todos/batch_complete`, {
      method: 'POST',
      body: {
        ids,
      },
    })
  },

  undoTodo: (todoId: number) => {
    return sendToApi<{ todo: Todo }>(`/todos/${todoId}/undo`, {
      method: 'PUT',
    })
  },

  pinTodo: (todoId: number) => {
    return sendToApi<{ todo: Todo }>(`/todos/${todoId}/pin`, {
      method: 'PUT',
    })
  },

  unpinTodo: (todoId: number) => {
    return sendToApi<{ todo: Todo }>(`/todos/${todoId}/unpin`, {
      method: 'PUT',
    })
  },

  getTodoSections: (workspaceId: number, generic: boolean) => {
    return sendToApi<{ task_sections: TaskSection[] }>(`/w/${workspaceId}/task_sections`, {
      params: { generic },
    })
  },

  createTodoSection: (workspaceId: number, sectionName: string, position: number | undefined) => {
    return sendToApi<{ task_section: TaskSection }>(`/w/${workspaceId}/task_sections`, {
      method: 'POST',
      body: {
        name: sectionName,
        position,
      },
    })
  },

  deleteTodoSection: (workspaceId: number, sectionId: number) =>
    sendToApi<null>(`/w/${workspaceId}/task_sections/${sectionId}`, {
      method: 'DELETE',
    }),

  deleteTodo: (todoId: number, trackArticleUpdate: boolean = false) => {
    return sendToApi<{ todo: Todo }>(`/todos/${todoId}`, {
      method: 'DELETE',
      body: {
        track_article_update: trackArticleUpdate,
      },
    })
  },

  updateTodoSection: (
    workspaceId: number | undefined,
    sectionId: number | undefined,
    input: UpdateTodoSectionInput,
  ) => {
    const { name, position } = input
    return sendToApi<{ task_section: TaskSection }>(`/w/${workspaceId}/task_sections/${sectionId}/`, {
      method: 'PUT',
      body: {
        section: {
          name,
          position,
        },
      },
    })
  },

  indexComments: (todoId: number, options: PaginationParams) => {
    return sendToApi<{ comments: Comment[] }>(`/todos/${todoId}/comments`, { params: options })
  },

  batchDelete: (uuids: string[] = [], ids: number[] = []) => {
    return sendToApi<{ todos: Todo[] }>('/todos/batch_delete', {
      method: 'DELETE',
      body: {
        uuids: uuids,
        ids: ids,
      },
    })
  },

  batchUndelete: (uuids: string[] = [], ids: number[] = []) => {
    return sendToApi<{ todos: Todo[] }>('/todos/batch_undelete', {
      method: 'PATCH',
      body: {
        uuids: uuids,
        ids: ids,
      },
    })
  },

  toggleReaction: (id: number, reaction: string) => {
    return sendToApi<{ todo: Todo }>(`/todos/${id}/toggle_reaction`, {
      method: 'POST',
      body: { reaction },
    })
  },
}
